import axios from "axios";
import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import { NotificationManager } from "react-notifications";
import { Tab, Tabs } from 'react-bootstrap';
import ProfileDetail from "./detailEntretien/ProfileDetail";
import ExperienceDetail from "./detailEntretien/ExperienceDetail";
import EtudeDetail from "./detailEntretien/EtudeDetail";
import LangueDetail from "./detailEntretien/LangueDetail";
import LoisirDetail from "./detailEntretien/LoisirDetail";
import InformatiqueDetail from "./detailEntretien/InformatiqueDetail";
import { Trash2, Ban, CheckCheck } from "lucide-react";

export default function DetailDemandeEntretien() {
    const formRef = useRef();
    const [entretien, setEntretien] = useState({});
    const { id } = useParams();

    useEffect(() => {
        const fetchEntretien = async () => {
            try {
                if (id) {
                    const resp = await axios.get(`entretiens/${id}`);
                    if (resp.status === 200) {
                        setEntretien(resp.data);
                    }
                }
            } catch (error) {
                console.error(error);
            }
        };
        fetchEntretien();
    }, [id]);

    const handleDelete = useCallback(async () => {
        if (window.confirm("Vous voulez vraiment le supprimer ?")) {
            try {
                const resp = await axios.delete(`entretiens/${id}`);
                if (resp.status === 204) {
                    NotificationManager.success("Vous avez supprimé cette demande d'entretien", "Demande supprimée", 4000);
                    window.history.back();
                }
            } catch (error) {
                console.error(error);
            }
        }
    }, [id]);

    const handleRefuse = useCallback(async () => {
        try {
            const resp = await axios.put(`entretiens/${id}`, { lu: false });
            if (resp.status === 200) {
                NotificationManager.success("Vous avez refusé cette demande d'entretien", "Demande refusée", 4000);
            }
        } catch (error) {
            console.error(error);
        }
    }, [id]);

    const handleValide = useCallback(async () => {
        try {
            const resp = await axios.put(`entretiens/${id}`, { lu: true });
            if (resp.status === 200) {
                NotificationManager.success("Vous avez validé cette demande d'entretien", "Demande validée", 4000);
            }
        } catch (error) {
            console.error(error);
        }
    }, [id]);

    return (
        <div className="adminx-content">
            <div className="adminx-main-content">
                <Tabulation 
                    data={entretien} 
                    handleDelete={handleDelete} 
                    handleValide={handleValide} 
                    handleRefuse={handleRefuse} 
                    formRef={formRef} 
                />
            </div>
        </div>
    );
}

function Tabulation({ data, handleValide, handleRefuse, handleDelete, formRef }) {
    const [user, setUser] = useState({});
    const [candidat, setCandidat] = useState({});
    const [experience, setExperience] = useState('');
    const [formation, setFormation] = useState('');
    const [langue, setLangue] = useState('');
    const [loisir, setLoisir] = useState('');
    const [informatique, setInformatique] = useState('');
    const [destinataireEmail, setDestinataireEmail] = useState('');

    useEffect(() => {
        const fetchUserData = async () => {
            try {
                if (data.user_id) {
                    const userResp = await axios.get(`users/${data.user_id}`);
                    if (userResp.status === 200) {
                        setUser(userResp.data.user);
                    }
                }
                if (data.cv_id) {
                    const cvResp = await axios.get(`cvs?id=${data.cv_id}`);
                    if (cvResp.status === 200) {
                        const { cv, exp, diplo, langage, loisir, info, email } = cvResp.data;
                        setCandidat(cvResp.data);
                        setDestinataireEmail(email);
                        setExperience(exp);
                        setFormation(diplo);
                        setLangue(langage);
                        setLoisir(loisir);
                        setInformatique(info);
                    }
                }
            } catch (error) {
                console.error(error);
            }
        };
        fetchUserData();
    }, [data.user_id, data.cv_id]);

    const handleChangeMail = (e) => {
        const selectedValue = e.target.value;
        setDestinataireEmail(selectedValue === "1" ? candidat.email : user.email);
    };

    const handleSubmitEmailNotification = async (e) => {
        e.preventDefault();
        try {
            const formData = new FormData(formRef.current);
            const resp = await axios.post('/notsendemail', formData);
            NotificationManager.success(resp.data.message, "Email", 4000);
        } catch (error) {
            NotificationManager.error(error.response?.data?.error || "Erreur", "Erreur", 4000);
        }
    };

    return (
        <div className="main-body">
            <div className="d-flex justify-content-between">
                <h4>Demande d'entretien</h4>
                <button type="button" className="btn btn-primary btn-sm" onClick={() => window.history.back()}>
                    <i className="bi bi-arrow-left-short"></i> Retour
                </button>
            </div>
            <hr />
            <div className={`alert ${data.lu ? 'alert-success' : 'alert-primary'}`} role="alert">
                <i className={`bi ${data.lu ? 'bi-check-all' : 'bi-caret-right'}`}></i>&nbsp;
                <b>{user.name} {user.prenom}</b> veut avoir un entretien avec <b>{candidat.nomPrenom} {candidat.prenom}</b> le {data.drdv} à {data.hrdv}.
            </div>
            <div className="btn-group">
                {data.lu ? (
                    <button type="button" onClick={handleDelete} className="btn btn-danger btn-sm">
                        <Trash2 size={20} strokeWidth={1.5} /> Supprimer la demande
                    </button>
                ) : (
                    <>
                        <button type="button" onClick={handleRefuse} className="btn btn-warning btn-sm mr-3">
                            <Ban size={20} strokeWidth={1.5} /> Refuser la demande
                        </button>
                        <button type="button" onClick={handleValide} className="btn btn-success btn-sm">
                            <CheckCheck size={20} strokeWidth={1.5} /> Valider la demande
                        </button>
                    </>
                )}
            </div>
            <hr />
            <div className="main-body">
                <div className="row gutters-sm">
                    <div className="col-md-4 mb-3">
                        <div className="card mb-grid">
                            <div className="card-header">
                                <div className="card-header-title">Envoyer un email</div>
                            </div>
                            <div className="card-body">
                                <div className="form-group">
                                    <div className="alert alert-warning" role="alert">
                                        <i className="bi bi-envelope-at-fill"></i>&nbsp;Veuillez choisir à qui envoyer l'email
                                    </div>
                                    <select className="form-control" onChange={handleChangeMail}>
                                        <option value="1">Candidat</option>
                                        <option value="2">Recruteur</option>
                                    </select>
                                </div>
                                <hr />
                                <form id="needs-validation" ref={formRef} onSubmit={handleSubmitEmailNotification}>
                                    <div className="form-row mb-3">
                                        <label className="form-label" htmlFor="mail">Adresse mail</label>
                                        <input
                                            type="email"
                                            className="form-control"
                                            id="mail"
                                            name="user_email"
                                            placeholder="Votre email"
                                            value={destinataireEmail}
                                            readOnly
                                        />
                                    </div>
                                    <div className="form-row mb-3">
                                        <label className="form-label" htmlFor="Object">Objet</label>
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="Object"
                                            placeholder="Objet de l'email"
                                            name="user_objet"
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label className="form-label" htmlFor="Textarea1">Message</label>
                                        <textarea className="form-control" id="Textarea1" rows="4" name="message"></textarea>
                                    </div>
                                    <button className="btn btn-primary mr-2" type="submit">
                                        <i className="bi bi-send"></i> Envoyer
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>
                    <div className="col-md-8">
                        <div className="card mb-3">
                            <div className="card-body">
                                <CandidatTabs 
                                    cv={candidat} 
                                    experience={experience} 
                                    formation={formation} 
                                    langue={langue} 
                                    loisir={loisir} 
                                    informatique={informatique} 
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function CandidatTabs({ cv, experience, formation, langue, loisir, informatique }) {
    return (
        <Tabs defaultActiveKey="profile" className="mb-3">
            <Tab eventKey="profile" title="Profile">
                <ProfileDetail profil={cv} />
            </Tab>
            <Tab eventKey="experience" title="Expérience">
                <ExperienceDetail exp={experience} />
            </Tab>
            <Tab eventKey="Etudes et Formation" title="Etudes et Formations">
                <EtudeDetail format={formation} />
            </Tab>
            <Tab eventKey="langue" title="Langue">
                <LangueDetail langu={langue} />
            </Tab>
            <Tab eventKey="Loisirs" title="Loisirs">
                <LoisirDetail loisir={loisir} />
            </Tab>
            <Tab eventKey="Informatique" title="Informatique">
                <InformatiqueDetail info={informatique} />
            </Tab>
        </Tabs>
    );
}
